import { i18n } from '@/translations/i18n';
import { EAppStep } from '@/application/session/EAppStep';
import { ZOOM_3D_DEFAULT_FACTOR } from '@/config';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import type { Product } from '@/domain/product/Product';
import type { CuttingShape } from '@/domain/cuttingShape/CuttingShape';
import type { TLocale } from '@/translations/i18n';
import type { TSide } from '@/domain/shared/TSide';
import type { IProductImage } from '@/domain/product/IProductImage';
import type { IFont } from '@/typescript/interfaces/IFont';
import type { IAppConfig } from '@/typescript/interfaces/IAppConfig';
import type { IProduct } from '@/domain/product/IProduct';
import type { TUploadImgType } from '@/typescript/interfaces/TUploadImgType';

const useConfigStore = defineStore('config', () => {
    const isMobile = ref(false);
    const isAppLoading = ref(true);
    const currentStep = ref(EAppStep.TEMPLATES);
    const fonts = ref<IFont[]>([]);
    const isAdmin = ref(false);
    const isAnimated = ref(false);
    const fZoom = ref(1);
    // Zoom of the default view. Stored next to fZoom so the zoom readout can be
    // a ratio of plain refs — reading it off the editor inside a computed made
    // the dependency conditional, and the readout froze on its first value.
    const fZoomDefault = ref(1);
    const zoom3dFactor = ref(ZOOM_3D_DEFAULT_FACTOR);
    // Derived, not stored: a second writer to a copy of the locale is what discarded the
    // language sent by the host -- initStore overwrote it right after reading it.
    const lang = computed<TLocale>(() => i18n.global.locale.value);
    const isStoreInitialized = ref(false);
    const displayGrid = ref(false);
    const isBATModalVisible = ref(false);
    const projectId = ref<string>();
    const isProductEdition = ref(false);
    const productImages = ref<IProductImage[]>([]);
    const cuttingShape = shallowRef<CuttingShape | undefined>();
    const currentSide = ref<TSide>('recto');
    const adminTemplateName = ref<string | undefined>(undefined);
    const isUploadModalVisible = ref(false);
    const uploadImgType = ref<TUploadImgType | undefined>();

    // The URL identifier used to look up the product (route param
    // :productName / API path segment). Set synchronously from the
    // route in bootstrap so parallel init tasks — template fetch,
    // graduation, camera model URL — can read it before the product
    // itself is loaded.
    //
    // NOT necessarily identical to `product.name` returned by the
    // backend — the API is free to canonicalise / alias / redirect,
    // so keep the two separate. Consumers that need "what the user
    // typed in the URL" read `productType`; consumers that need "what
    // the backend calls this record" read `productName` (below).
    const productType = ref<IAppConfig['product_type']>('cup_33');

    // The loaded product from the backend. Null until initStore
    // resolves. The typed getters below derive off this — writing to
    // one of them is not supported, use patchProduct() for runtime
    // mutations (admin uploads).
    const product = shallowRef<Product | null>(null);

    // Runtime patch on the loaded product. Covers admin uploads
    // (cuttingPath, helpGridRecto, helpGridVerso) — the derived
    // getters reflect the change. No-op if the product isn't loaded
    // yet, since a patch has nothing to merge onto.
    function patchProduct(patch: Partial<IProduct>) {
        if (!product.value) {
            return;
        }
        product.value = product.value.with(patch);
    }

    function setDisplayGrid(value: boolean) {
        displayGrid.value = value;
    }

    return {
        adminTemplateName,
        currentSide,
        currentStep,
        cuttingShape,
        displayGrid,
        fonts,
        fZoom,
        fZoomDefault,
        isAdmin,
        isAnimated,
        isAppLoading,
        isBATModalVisible,
        isMobile,
        isProductEdition,
        isStoreInitialized,
        isUploadModalVisible,
        lang,
        patchProduct,
        product,
        productImages,
        productType,
        projectId,
        setDisplayGrid,
        uploadImgType,
        zoom3dFactor,
    };
});

export { useConfigStore };
